import React, { createContext, useContext, useState, useEffect } from 'react';

const AdminContext = createContext({
    isAdmin: false,
    loading: true,
    checkAdminStatus: () => { }
});

// Environment-based API URL configuration
const SERVER_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:5000/' : '/api/';

export const AdminProvider = ({ children }) => {
    const [isAdmin, setIsAdmin] = useState(false);
    const [loading, setLoading] = useState(true);

    const checkAdminStatus = async () => {
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 3000);

            const response = await fetch(`${SERVER_URL}user/is-admin`, {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                signal: controller.signal
            });

            clearTimeout(timeoutId);
            const data = await response.json();

            if (response.ok && data.status) {
                setIsAdmin(data.is_admin || false);
            } else {
                setIsAdmin(false);
            }
        } catch (err) {
            if (err.name === 'AbortError') {
                console.warn('Admin check timed out');
            } else {
                console.warn('Error checking admin status:', err);
            }
            setIsAdmin(false);
        } finally {
            setLoading(false);
        }
    };

    // Only check admin status when on authenticated routes
    useEffect(() => {
        const path = window.location.pathname;
        // Only check if we're on an authenticated route, not on login page
        if (path === '/tic-bot' || path === '/admin-dashboard') {
            checkAdminStatus();
        } else {
            // On login/unauthenticated pages, just set loading to false
            setLoading(false);
        }
    }, []);

    return (
        <AdminContext.Provider value={{ isAdmin, loading, checkAdminStatus }}>
            {children}
        </AdminContext.Provider>
    );
};

export const useAdmin = () => {
    const context = useContext(AdminContext);
    if (!context) {
        throw new Error('useAdmin must be used within AdminProvider');
    }
    return context;
};

export default AdminContext;
